import { Tag, type TagProps } from '@kanda-libs/ks-design-library';
import {
  JobCreditState,
  type FinanceStatus,
  type Job,
} from '@kanda-libs/ks-frontend-services';
import JobExampleTag from 'components/JobExample';
import JobFinanceStatus from 'components/JobFinanceStatus';
import JobStatus from 'components/JobStatus';
import { useMemo } from 'react';

export interface JobCombinedStatusProps {
  status?: Job['status'];
  size?: TagProps['size'];
  financeStatus?: FinanceStatus;
  checkJobStatus?: JobCreditState['current_status'];
  archived?: boolean;
  example?: boolean;
  isLoading?: boolean;
  paidByBank?: boolean;
  changesRequested?: boolean;
}

const JobCombinedStatus: React.FunctionComponent<JobCombinedStatusProps> =
  function ({
    status,
    financeStatus,
    checkJobStatus,
    size = 21,
    archived = false,
    example = false,
    isLoading = false,
    paidByBank = false,
    changesRequested = false,
  }) {
    const depositRequired = useMemo(
      () =>
        checkJobStatus === 'accepted_deposit_required' &&
        (financeStatus === 'applied_for_finance' ||
          financeStatus === 'financed'),
      [checkJobStatus, financeStatus],
    );
    const showPaidByBank = useMemo(() => {
      if (status === 'finished') return false;
      return paidByBank;
    }, [status, paidByBank]);

    return (
      <div className="flex flex-row flex-wrap md:flex-nowrap gap-x-1 md:gap-x-2 whitespace-nowrap align-end justify-end md:align-start md:justify-start my-auto">
        {example && (
          <div className="flex ml-auto md:ml-0">
            <JobExampleTag isLoading={isLoading} size={size} />
          </div>
        )}
        {status && status !== 'draft' && (
          <div className="flex ml-auto md:ml-0">
            <JobStatus
              status={status}
              archived={archived}
              isLoading={isLoading}
              size={size}
            />
          </div>
        )}
        {changesRequested && (
          <div className="flex ml-auto md:ml-0">
            <Tag
              size={size}
              label="Changes Requested"
              color="orange"
              variant="solid"
            />
          </div>
        )}
        {financeStatus &&
          !showPaidByBank &&
          !depositRequired &&
          !changesRequested && (
            <div className="flex ml-auto md:ml-0">
              <JobFinanceStatus
                financeStatus={financeStatus}
                archived={archived}
                isLoading={isLoading}
                size={size}
              />
            </div>
          )}
        {depositRequired && !isLoading && (
          <div className="flex ml-auto md:ml-0">
            <Tag
              size={size}
              label="Deposit Unpaid"
              color="blue"
              variant="solid"
            />
          </div>
        )}
        {showPaidByBank && !isLoading && (
          <div className="flex ml-auto md:ml-0">
            <Tag
              size={size}
              label="Paid open banking"
              color="green"
              variant="solid"
            />
          </div>
        )}
      </div>
    );
  };

export default JobCombinedStatus;
